import React, { useCallback, useState } from "react";
import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  useBoolean,
} from "@chakra-ui/react";
import useTranslate from "hooks/useTranslate";
import useDressingRoom from "hooks/useDressingRoom";
import TraitTile from "components/TraitTile";
import { FaTimes } from "react-icons/fa";
import StakePopup from "components/StakePopup";
import useTraits from "hooks/useTraits";
import TraitsList from "components/TraitsList";
import useV2 from "hooks/useV2";
import StakeSuccessPopup from "components/StakeSuccessPopup";
import useAssets from "hooks/useAssets";

const TraitsHangerContainer = () => {
  const translate = useTranslate();
  const { traitsByTokenId } = useTraits();
  const { tokenId } = useV2();
  const { v2sInWallet } = useAssets();
  const { originalTraits, changedTraits, removeTrait } = useDressingRoom();
  const [selectedTraits, setSelectedTraits] = useState<number[]>([]);
  const [isStakePopupOpen, setIsStakePopupOpen] = useBoolean();
  const [isStakeSuccessPopupOpen, setIsStakeSuccessPopupOpen] = useBoolean();

  const isOwner =
    tokenId && v2sInWallet?.length && v2sInWallet.includes(tokenId);

  const isMultiple = (changedTraits?.length || 0) > 1;

  const handleStake = useCallback(
    (tokenId: number) => {
      setSelectedTraits([tokenId]);
      setIsStakePopupOpen.on();
    },
    [setSelectedTraits, setIsStakePopupOpen]
  );

  const handleStakeAll = useCallback(() => {
    setSelectedTraits(changedTraits!);
    setIsStakePopupOpen.on();
  }, [changedTraits, setSelectedTraits, setIsStakePopupOpen]);

  const handleStakeSuccess = useCallback(() => {
    setIsStakePopupOpen.off();
    setIsStakeSuccessPopupOpen.on();
  }, [setIsStakePopupOpen, setIsStakeSuccessPopupOpen]);

  return (
    <>
      {isOwner && changedTraits && changedTraits.length > 0 ? (
        <Box w="full">
          <TraitsList
            title={translate("traitsShelf:title")}
            borderBottomRadius={isMultiple ? "none" : undefined}
          >
            {changedTraits.map((tokenId) => {
              const trait = traitsByTokenId[tokenId];

              return trait ? (
                <Box key={tokenId} w="full">
                  <TraitTile
                    tokenId={tokenId}
                    name={trait.name}
                    canvasSize={{
                      base: "12",
                      sm: "14",
                    }}
                  >
                    <HStack spacing="3" p="1.5">
                      <Button size="sm" onClick={() => handleStake(tokenId)}>
                        {translate("common:stake")}
                      </Button>

                      <IconButton
                        size="sm"
                        aria-label=""
                        icon={<Icon as={FaTimes} />}
                        borderRadius="full"
                        colorScheme="dark"
                        onClick={() => removeTrait(tokenId)}
                      />
                    </HStack>
                  </TraitTile>
                </Box>
              ) : null;
            })}
          </TraitsList>

          {isMultiple && (
            <Box p="3" pt="0" borderBottomRadius="md" bg="dark.900">
              <Button w="full" onClick={handleStakeAll}>
                {translate("traitsShelf:stakeAll", {
                  count: changedTraits.length,
                })}
              </Button>
            </Box>
          )}
        </Box>
      ) : null}

      {selectedTraits && isStakePopupOpen && (
        <StakePopup
          isOpen
          tokenId={tokenId!}
          currentTraits={originalTraits}
          traitsToStake={selectedTraits}
          onClose={setIsStakePopupOpen.off}
          onSuccess={handleStakeSuccess}
        />
      )}

      {selectedTraits && isStakeSuccessPopupOpen && (
        <StakeSuccessPopup
          isOpen
          title={translate("stake:success:title")}
          description={translate("stake:success:description")}
          tokenId={tokenId!}
          withConfetti
          currentTraits={originalTraits}
          newTraits={selectedTraits}
        />
      )}
    </>
  );
};

export default TraitsHangerContainer;
